
import React from 'react';
import { Calendar } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import DisplayStatusBadge from './DisplayStatusBadge';
import { DisplaySchedule, LegacySchedule, EnhancedSchedule } from '@/types/display';

interface DisplayScheduleCardProps {
  schedule: DisplaySchedule;
  city?: string | null;
  state?: string | null;
  timezone?: string;
}

const DisplayScheduleCard: React.FC<DisplayScheduleCardProps> = ({ 
  schedule, 
  city, 
  state, 
  timezone 
}) => {
  // Type guards
  const isEnhancedSchedule = (schedule: DisplaySchedule): schedule is EnhancedSchedule => {
    return schedule !== null && 'timezone' in schedule && 'regular_hours' in schedule;
  };

  const isLegacySchedule = (schedule: DisplaySchedule): schedule is LegacySchedule => {
    return schedule !== null && 'start_date' in schedule && 'end_date' in schedule;
  };

  // Format functions for legacy schedule
  const formatLegacySchedule = (schedule: LegacySchedule): string => {
    const startDate = new Date(schedule.start_date);
    const endDate = new Date(schedule.end_date);
    
    const startFormatted = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const endFormatted = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  const formatLegacyHours = (schedule: LegacySchedule): string => {
    if (!schedule.hours) return 'Hours not available';
    return `${schedule.hours.start.substring(0, 5)} - ${schedule.hours.end.substring(0, 5)}`;
  };

  const formatLegacyDays = (schedule: LegacySchedule): string => {
    if (!schedule.days || schedule.days.length === 0) return 'Days not available';
    
    if (schedule.days.length === 7) {
      return 'Every day';
    }
    
    return schedule.days.join(', ');
  };

  // Format functions for enhanced schedule
  const formatEnhancedSeason = (schedule: EnhancedSchedule): string => {
    if (!schedule.season_dates) return 'Year-round display';
    
    const startDate = new Date(schedule.season_dates.start);
    const endDate = new Date(schedule.season_dates.end);
    
    const startFormatted = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const endFormatted = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  const formatEnhancedHours = (schedule: EnhancedSchedule): string => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayAbbrevs = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    // Group days with same hours
    const hourGroups: { [key: string]: string[] } = {};
    
    days.forEach((day, index) => {
      const hours = schedule.regular_hours[day];
      if (hours) {
        const timeKey = `${hours.start}-${hours.end}`;
        if (!hourGroups[timeKey]) {
          hourGroups[timeKey] = [];
        }
        hourGroups[timeKey].push(dayAbbrevs[index]);
      }
    });

    if (Object.keys(hourGroups).length === 0) {
      return 'No regular hours set';
    }

    // Format grouped hours
    return Object.entries(hourGroups)
      .map(([timeRange, days]) => {
        const [start, end] = timeRange.split('-');
        const formattedTime = `${start.substring(0, 5)} - ${end.substring(0, 5)}`;
        
        if (days.length === 7) {
          return `Daily: ${formattedTime}`;
        } else if (days.length === 5 && !days.includes('Sat') && !days.includes('Sun')) {
          return `Weekdays: ${formattedTime}`;
        } else if (days.length === 2 && days.includes('Sat') && days.includes('Sun')) {
          return `Weekends: ${formattedTime}`;
        } else {
          return `${days.join(', ')}: ${formattedTime}`;
        }
      })
      .join('\n');
  };

  const formatEnhancedDays = (schedule: EnhancedSchedule): string => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const activeDays = days.filter(day => schedule.regular_hours[day] !== null);
    
    if (activeDays.length === 0) return 'No scheduled days';
    if (activeDays.length === 7) return 'Every day';
    
    const dayNames = activeDays.map(day => day.charAt(0).toUpperCase() + day.slice(1, 3));
    return dayNames.join(', ');
  };

  if (!schedule) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center">
            <Calendar className="mr-2" size={18} />
            Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <DisplayStatusBadge 
              schedule={schedule}
              city={city}
              state={state}
              timezone={timezone}
              variant="detailed"
              showRefresh={true}
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center">
          <Calendar className="mr-2" size={18} />
          Schedule
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Status Badge */}
          <DisplayStatusBadge 
            schedule={schedule}
            city={city}
            state={state}
            timezone={timezone}
            variant="detailed"
            showRefresh={true}
          />
          
          <Separator />
          
          {/* Schedule Details */}
          {isLegacySchedule(schedule) && (
            <>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Season</div>
                <div className="font-medium">{formatLegacySchedule(schedule)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Days</div>
                <div className="font-medium">{formatLegacyDays(schedule)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Hours</div>
                <div className="font-medium">{formatLegacyHours(schedule)}</div>
              </div>
            </>
          )}
          
          {isEnhancedSchedule(schedule) && (
            <>
              {schedule.season_dates && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Season</div>
                  <div className="font-medium">{formatEnhancedSeason(schedule)}</div>
                </div>
              )}
              <div>
                <div className="text-sm text-muted-foreground mb-1">Days</div>
                <div className="font-medium">{formatEnhancedDays(schedule)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Hours</div>
                <div className="font-medium whitespace-pre-line">{formatEnhancedHours(schedule)}</div>
              </div>
              {schedule.timezone && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Timezone</div>
                  <div className="font-medium text-sm">
                    {schedule.timezone.replace('_', ' ').split('/')[1] || schedule.timezone}
                  </div>
                </div>
              )}
              {schedule.date_overrides && Object.keys(schedule.date_overrides).length > 0 && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Special Dates</div>
                  <div className="text-sm space-y-1">
                    {Object.entries(schedule.date_overrides).map(([date, hours]) => (
                      <div key={date} className="flex justify-between">
                        <span>{new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        <span className="text-muted-foreground">
                          {hours ? `${hours.start.substring(0, 5)} - ${hours.end.substring(0, 5)}` : 'Closed'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {schedule.migrated_from_text && schedule.original_hours && (
                <div className="text-xs text-muted-foreground pt-2 border-t">
                  Original: "{schedule.original_hours}"
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DisplayScheduleCard;
